import { Trans, useTranslation } from "react-i18next";
import AccountHeader from "../../AccountHeader";
import { Container, Stack, Typography, type ChipProps } from "@mui/material";
import { type ReactElement, useMemo } from "react";
import tKey from "../../../Common/Utils/tKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { useSearchParams } from "react-router";
import useStoreFlagError from "../../../Common/_hooks/Store/useStoreFlagError";
import StoreSetupCard from "./StoreSetupCard";
import StoreShippingTablesEmbed from "./StoreShippingTablesEmbed";
import StoreConnectionStepper from "./StoreConnectionStepper";
import useStoreData from "../../../Common/_hooks/Store/useStoreData";

export default function EtsyConnect() {
  const [t] = useTranslation("account-v2");
  const [tR] = useTranslation("routes");

  const [searchParams] = useSearchParams();

  const storeSlug = "etsy";
  const setupUrl = tR("/account/stores/connect/{{store}}", { store: storeSlug });

  const store = useStoreData(storeSlug);

  const isFlagged = useMemo(() => store?.get("flagged"), [store]);
  const isMissingScope = !!store?.get("is_missing_scope");
  const flagErrorMessage = useStoreFlagError(storeSlug);
  const isConnected = !!store && !isFlagged && !isMissingScope;
  const connectUrl = `${setupUrl}?connect=1`;
  const startConnectUrl = tR("/d/{{store}}/connect", { store: storeSlug }) + "?connect=1";

  const connectErrorMessage = useMemo(() => {
    const error = searchParams.get("error");
    if (!error) return null;

    switch (error) {
      case "access_denied":
        return t("It seems like you denied access to your store for Art of Where. In order to complete the connection process, make sure you follow the steps below and click Grant access.");
      case "already-exists":
        return t("This Etsy store is already linked to another Art of Where account. Please remove it from the other account first.");
      default:
        return t("An error occured while connecting to your store. Please try again.");
    }
  }, [searchParams, t]);

  const connectionStatus = useMemo((): { label: string, color: ChipProps["color"], icon: ReactElement } => {
    if (isConnected) {
      return {
        label: t("Connected"),
        color: "success",
        icon: <CheckCircleIcon color="success" />,
      };
    }

    if (isFlagged) {
      return {
        label: t("Needs attention"),
        color: "error",
        icon: <ErrorOutlineRoundedIcon color="error" />,
      };
    }

    if (isMissingScope) {
      return {
        label: t("Needs attention"),
        color: "warning",
        icon: <ErrorOutlineRoundedIcon color="warning" />,
      };
    }

    return {
      label: t("Not connected"),
      color: "default",
      icon: <RadioButtonUncheckedRoundedIcon color="action" />,
    };
  }, [isConnected, isFlagged, isMissingScope, t]);

  return <div>
    <AccountHeader
      title={t("Set up your {{store}} store", { store: "Etsy" })}
    />

    <Container
      maxWidth="md"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={4}>
        <Typography variant="body1">
          {t("Connect your Etsy shop first, then review the shipping details below so you know how rates will work.")}
        </Typography>

        <StoreSetupCard
          icon={connectionStatus.icon}
          title={t("Connection")}
          status={connectionStatus.label}
          statusColor={connectionStatus.color}
        >
          <StoreConnectionStepper
            t={t}
            hasStoreStepLabel={t("Do you already have an Etsy shop?")}
            noStoreTitle={t("Create your Etsy shop")}
            noStoreDescription={<Typography variant="body1">
              <Trans
                t={t}
                i18nKey={tKey("Go to <a>Etsy</a> and create your shop now. Make sure your seller account is active before you continue.")}
                components={{
                  a: <a href="https://www.etsy.com/" target="_blank" />
                }}
              />
            </Typography>}
            connectStepLabel={t("Connect your Etsy shop to Art of Where")}
            connectSteps={[
              t("Click the button below to start the Etsy authorization flow."),
              t("Sign in to your Etsy account if you are prompted to do so."),
              t("Click Grant access to authorize Art of Where to access your shop."),
            ]}
            connectButtonLabel={t("Connect to Etsy")}
            connectUrl={connectUrl}
            startConnectRequested={searchParams.get("connect") === "1"}
            startConnectUrl={startConnectUrl}
            connectErrorMessage={connectErrorMessage}
            isConnected={isConnected}
            isFlagged={!!isFlagged}
            isMissingScope={isMissingScope}
            flagErrorMessage={flagErrorMessage}
            connectedContent={<Typography variant="body1">
              {t("Your Etsy shop is connected and ready for product publishing and order syncing.")}
            </Typography>}
          />
        </StoreSetupCard>

        <StoreSetupCard
          icon={<LocalShippingOutlinedIcon color="secondary" />}
          title={t("Shipping")}
          status={isConnected ? t("Handled automatically") : t("Automatic after connection")}
          statusColor={isConnected ? "success" : "default"}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body1">
              {t("You do not need to pre-configure shipping in Etsy for products published from Art of Where.")}
            </Typography>

            <Typography variant="body2">
              {t("When you publish products from Art of Where, we automatically create and publish the Etsy shipping profiles those products need. You can still check the shipping tables below if you want to review the rates behind them.")}
            </Typography>

            <StoreShippingTablesEmbed
              defaultView="product"
              allowedViews={["product"]}
              typeOptionsByCountry={{
                ca: [{ label: t("Standard"), value: "canada-expedited-parcel" }],
                us: [{ label: t("Standard"), value: "us-standard", zoneSourceValue: "usps-priority-mail" }],
                in: [
                  { label: t("International Tracked"), value: "intl-tracked" },
                  { label: t("DHL Express Worldwide"), value: "dhl-express-worldwide" },
                ],
              }}
            />
          </Stack>
        </StoreSetupCard>
      </Stack>
    </Container>
  </div>;
}
